import React from 'react';
import logger from '@3dera/core/utils/logger';

/**
 * 🛡️ Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the component tree
 * and displays a fallback UI instead of crashing the app.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to console (in production, send to error tracking service)
        logger.error('🚨 Application Error Caught by Error Boundary:', {
            error: error.toString(),
            componentStack: errorInfo.componentStack
        });

        // Deploy sonrası chunk load hatalarında otomatik sayfa yenileme
        const msg = error?.message || '';
        const isChunkError =
            msg.includes('Failed to fetch dynamically imported module') ||
            msg.includes('Importing a module script failed') ||
            msg.includes('Failed to load module script') ||
            msg.includes('error loading dynamically imported module') ||
            error?.name === 'ChunkLoadError';

        if (isChunkError) {
            const key = 'eb_chunk_reload';
            const lastReload = sessionStorage.getItem(key);
            if (!lastReload) {
                sessionStorage.setItem(key, Date.now().toString());
                window.location.reload();
                return;
            }
            // Zaten reload yaptık — temizle, hata sayfasını göster
            sessionStorage.removeItem(key);
        }

        this.setState({
            errorInfo
        });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    backgroundColor: 'var(--bg-color, #000)',
                    color: 'var(--text-color, #fff)',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                    <div style={{
                        maxWidth: '600px',
                        textAlign: 'center',
                        background: 'rgba(255, 255, 255, 0.05)',
                        padding: '40px',
                        borderRadius: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>

                        <h1 style={{
                            fontSize: '32px',
                            marginBottom: '16px',
                            color: '#ff6b6b'
                        }}>
                            Bir Hata Oluştu
                        </h1>

                        <p style={{
                            fontSize: '16px',
                            marginBottom: '30px',
                            color: 'rgba(255, 255, 255, 0.7)',
                            lineHeight: '1.6'
                        }}>
                            Üzgünüz, beklenmeyen bir hata oluştu. Bu bizim hatamız.
                            Lütfen sayfayı yenileyin veya ana sayfaya dönün.
                        </p>

                        {/* Show error details in development mode */}
                        {import.meta.env.MODE === 'development' && this.state.error && (
                            <details style={{
                                textAlign: 'left',
                                marginBottom: '20px',
                                padding: '15px',
                                background: 'rgba(255, 0, 0, 0.1)',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontFamily: 'monospace'
                            }}>
                                <summary style={{ cursor: 'pointer', marginBottom: '10px', fontWeight: 'bold' }}>
                                    🐛 Hata Detayları (Sadece Development)
                                </summary>
                                <div style={{ whiteSpace: 'pre-wrap', overflow: 'auto' }}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                                </div>
                            </details>
                        )}

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={this.handleReload}
                                style={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    background: 'linear-gradient(135deg, #8408c2 0%, #ed51f5 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                }}
                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                                🔄 Sayfayı Yenile
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                style={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                }}
                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                                🏠 Ana Sayfaya Dön
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
